import { demographicReportPermissionsMiddleware, platformPermissionsMiddleware } from '@/helpers/routeMiddleware';
import Account from '@/components/settings/account/Account.vue';
import CustomFields from '@/components/settings/CustomFields.vue';
import AcknowledgementSettings from '@/components/settings/AcknowledgementSettings.vue';
import AiSettings from '@/components/settings/AiSettings.vue';
import JobPostSettings from '@/components/settings/JobPostSettings.vue';
import JobProfileSettings from '@/components/settings/JobProfileSettings.vue';
import Integrations from '@/components/settings/integrations/Integrations.vue';
import Notifications from '@/components/settings/Notifications.vue';
import OrganizationSettings from '@/components/settings/OrganizationSettings.vue';
import PolicyGuide from '@/components/settings/policyGuide/PolicyGuide.vue';
import DemographicReportPermissions from '@/components/settings/team/views/DemographicReportPermissions.vue';
import DatapeopleAdministrators from '@/components/settings/team/views/DatapeopleAdministrators.vue';
import PlatformPermissions from '@/components/settings/team/views/PlatformPermissions.vue';
import Team from '@/components/settings/team/Team.vue';
import Templates from '@/components/settings/templates/Templates.vue';
import StyleGuide from '@/components/settings/styleGuide/StyleGuide.vue';
import SsoSetup from '@/components/settings/ssoSetup/SsoSetup.vue';
import UserRoles from '@/components/settings/UserRoles.vue';
import Settings from '@/views/Settings.vue';

export const settings = (authDataStore) => ([
  {
    path: '/settings/',
    name: 'settings',
    component: Settings,
    redirect: { name: 'account' },
    meta: { title: 'Settings' },
    children: [
      {
        name: 'templates',
        path: 'templates/:from?',
        props: true,
        meta: { timer: 3000, title: 'Templates' },
        component: Templates,
        beforeEnter: async (to, from, next) => {
          if (authDataStore.authData.canSeeTemplates) {
            next();
          } else {
            next({ name: 'root' });
          }
        },
      },
      {
        name: 'account',
        path: 'account',
        meta: { timer: 3000, title: 'Account' },
        component: Account,
      },
      {
        name: 'sso-setup-old',
        path: 'sso-setup',
        redirect: () => ({ name: 'sso-setup' }),
      },
      {
        name: 'notifications',
        path: 'notifications',
        meta: { timer: 3000, title: 'Notifications' },
        component: Notifications,
      },
      {
        name: 'integrations',
        path: 'integrations',
        meta: { timer: 3000, title: 'Connect Your ATS' },
        component: Integrations,
      },
      {
        name: 'team',
        path: 'team',
        meta: { timer: 3000, title: 'Team' },
        component: Team,
        redirect: { name: 'administrators' },
        children: [
          {
            name: 'administrators',
            path: 'administrators',
            meta: { timer: 3000, title: 'My Datapeople Administrators' },
            component: DatapeopleAdministrators,
          },
          {
            name: 'platform-permissions',
            path: 'platform-permissions',
            meta: { timer: 3000, title: 'Platform Permissions' },
            component: PlatformPermissions,
            beforeEnter: platformPermissionsMiddleware(authDataStore),
          },
          {
            name: 'demographic-permissions',
            path: 'demographic-permissions',
            meta: { timer: 3000, title: 'Demographic Report Permissions' },
            component: DemographicReportPermissions,
            beforeEnter: demographicReportPermissionsMiddleware(authDataStore),
          },
        ],
      },
      {
        name: 'usage-settings',
        path: 'usage',
        redirect: (to) => ({ name: 'usage', query: { ...to.query, legacy: 'true' } }),
      },
      {
        name: 'organization-settings',
        path: 'organization-settings',
        component: OrganizationSettings,
        redirect: () => ({ name: 'sso-setup' }),
        children: [
          {
            name: 'sso-setup',
            path: 'sso-setup',
            meta: { timer: 3000, title: 'Organization Settings', parentTitle: 'Settings' },
            component: SsoSetup,
            beforeEnter: async (to, from, next) => {
              if (authDataStore.authData.isSuperuser || authDataStore.authData.canTeamAdmin) {
                next();
              } else {
                next({ name: 'settings' });
              }
            },
          },
          {
            name: 'user-roles',
            path: 'user-roles',
            meta: { timer: 3000, title: 'Organization Settings', parentTitle: 'Settings' },
            component: UserRoles,
            beforeEnter: async (to, from, next) => {
              if (authDataStore.authData.isSuperuser || authDataStore.authData.canGreenhouseRoles) {
                next();
              } else {
                next({ name: 'settings' });
              }
            },
          },
          {
            name: 'custom-fields',
            path: 'custom-fields',
            meta: { timer: 3000, title: 'Organization Settings', parentTitle: 'Settings' },
            component: CustomFields,
            beforeEnter: async (to, from, next) => {
              if (authDataStore.authData.isSuperuser || authDataStore.authData.canCustomFields) {
                next();
              } else {
                next({ name: 'settings' });
              }
            },
          },
          {
            name: 'ai-settings',
            path: 'ai-settings',
            meta: { timer: 3000, title: 'Organization Settings', parentTitle: 'Settings' },
            component: AiSettings,
            beforeEnter: async (to, from, next) => {
              if (authDataStore.authData.canTeamAdmin) {
                next();
              } else {
                next({ name: 'settings' });
              }
            },
          },
          {
            name: 'job-post-settings',
            path: 'job-post-settings',
            meta: { timer: 3000, title: 'Organization Settings', parentTitle: 'Settings' },
            component: JobPostSettings,
            beforeEnter: async (to, from, next) => {
              if (authDataStore.authData.canTeamAdmin) {
                next();
              } else {
                next({ name: 'settings' });
              }
            },
          },
          {
            name: 'job-profile-settings',
            path: 'job-profile-settings',
            meta: { timer: 3000, title: 'Organization Settings', parentTitle: 'Settings' },
            component: JobProfileSettings,
            beforeEnter: (to, from, next) => {
              const { jobProfileEnabled, canTeamAdmin } = authDataStore.authData;

              if (jobProfileEnabled && canTeamAdmin) {
                next();
              } else {
                next({ name: 'settings' });
              }
            },
          },
          {
            name: 'acknowledgements-settings',
            path: 'acknowledgements-settings',
            meta: { timer: 3000, title: 'Organization Settings', parentTitle: 'Settings' },
            component: AcknowledgementSettings,
            beforeEnter: (to, from, next) => {
              const { jobProfileEnabled, canTeamAdmin, acknowledgementsEnabled } = authDataStore.authData;

              if (jobProfileEnabled && canTeamAdmin && acknowledgementsEnabled) {
                next();
              } else {
                next({ name: 'settings' });
              }
            },
          },
        ],
      },
      {
        name: 'user-roles-old',
        path: 'user-roles',
        redirect: () => ({ name: 'user-roles' }),
      },
      {
        name: 'custom-fields-old',
        path: 'custom-fields',
        redirect: () => ({ name: 'custom-fields' }),
      },
      {
        name: 'style-guide',
        path: 'style-guide',
        meta: { timer: 3000, title: 'Style Guide' },
        component: StyleGuide,
        beforeEnter: async (to, from, next) => {
          if (authDataStore.authData.canTeamAdmin) {
            next();
          } else {
            next({ name: 'settings' });
          }
        },
      },
      {
        name: 'policy-guide',
        path: 'policy-guide',
        meta: { timer: 3000, title: 'Policy Guide' },
        component: PolicyGuide,
        beforeEnter: async (to, from, next) => {
          if (authDataStore.authData.canTeamAdmin) {
            next();
          } else {
            next({ name: 'settings' });
          }
        },
      },
    ],
  },
]);
